import { Group, Text, Textarea } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";

import type { SourceRef, TemplateSource } from "../../../api/generated/model";
import { VideoMotionQuote, VideoMotionRequest } from "../../../api/generated/zod";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { useGenerateImage } from "../../../platform/media/GenerateImageDialog";
import { pictureOf } from "../../../platform/media/ImagePicker";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import {
  Errors,
  ROUTE,
  StartGroup,
  defaultModel,
  rowsOf,
  type ToolFormProps,
} from "./form-parts";
import { imageLimitLines, videoLimitLines } from "../video/limits";
import { ToolSlots } from "./ToolSlots";
import { useToolMedia } from "./use-tool-video";

/**
 * Live's first Motion Control placeholder (`placeholders.ts`). Live types its three in turn;
 * ours shows the first (G7-tool-forms difference).
 */
const PLACEHOLDER =
  "Apply the movement from the reference video to the main character. Keep the character's appearance, clothing, environment, and camera angle unchanged.";

/**
 * Live's Motion Control form (#214, `motion-control-form`, capture 10, G7-video-tools): "Image
 * refs" (the character, from the library, Generate, an upload or a product's photo) and "Video
 * ref" (the motion); the prompt, optional, to the model's 2,500 characters; the model; the price
 * and the start, grey until both files are in, as live's needs both.
 */
export function MotionForm({ catalog, above, overlay, onStarted, clone, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.motion");
  // A clone's model where it is still offered, else the default (issue 828, item 23).
  const [modelId, setModelId] = useState(
    () => models.find((each) => each.id === clone?.model)?.id ?? defaultModel(models).id,
  );
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const promptMax = model.modes["video.motion"]?.prompt_max_chars ?? model.prompt_max_chars;
  // Live's slot tooltips: the mode's media limits (#302) as live's lines (motion-control-form's
  // image-refs-input and video-refs-input), none where it has none (issue 461).
  const limits = model.modes["video.motion"]?.media_limits;
  const [prompt, setPrompt] = useState(clone?.params?.prompt ?? "");
  const [collapsed, setCollapsed] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const character = useToolMedia(teamId, brandId, "image", clonedFile(clone?.params?.start_frame));
  const motion = useToolMedia(teamId, brandId, "video", clonedFile(clone?.params?.source_video));
  // Live's "Generate" on the character's slot (#985, its `ImageRefsInput`'s `sources.generate`).
  const generate = useGenerateImage({
    teamId,
    brandId,
    onSaved: (asset) =>
      character.choose({
        assetId: asset.id,
        url: pictureOf(asset),
        name: asset.name,
        width: asset.width,
        height: asset.height,
      }),
  });
  const fields = {
    type: "video.motion" as const,
    model: model.id,
    ...(character.ref === null ? {} : { start_frame: character.ref }),
    ...(motion.ref === null ? {} : { source_video: motion.ref }),
    // Live sends the prompt only when it has text.
    ...(prompt.trim() === "" ? {} : { prompt }),
  };
  const quote = VideoMotionQuote.safeParse(fields);
  const request = VideoMotionRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const image = character.video;
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={
        <ProductPicker
          teamId={teamId}
          brandId={brandId}
          chosen={null}
          opened={productsOpen}
          onClose={() => setProductsOpen(false)}
          onConfirm={(choice) =>
            character.choose({
              assetId: choice.assetId,
              url: choice.url,
              name: choice.name,
              width: null,
              height: null,
            })
          }
          anchor={(wrap) => (
            <ToolSlots
              elements={undefined}
              refs={{
                images: image === null ? [] : [image],
                limits: imageLimitLines(limits?.image),
                tagged: false,
                disabled: false,
                full: image !== null,
                onFromLibrary: character.slot.onFromLibrary,
                onGenerate: generate.open,
                onFromProduct: () => setProductsOpen(true),
                onUpload: (files) => character.slot.onUpload(files[0] ?? null),
                uploading: character.slot.uploading,
                onRemove: character.slot.onRemove,
                wrap,
              }}
              video={{ ...motion.slot, limits: videoLimitLines(limits?.video) }}
            />
          )}
        />
      }
      summary={<span>Motion Control</span>}
      above={above}
      overlay={overlay}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <ModelPicker models={models} value={model.id} onChange={setModelId} />
          <StartGroup job={job} notes={model.modes["video.motion"]?.notes ?? []} />
        </Group>
      }
    >
      <Textarea
        variant="unstyled"
        aria-label="Prompt"
        placeholder={PLACEHOLDER}
        autosize
        minRows={1}
        maxRows={5}
        maxLength={promptMax}
        value={prompt}
        onChange={(event) => setPrompt(event.currentTarget.value)}
      />
      <Text size="xs" c="var(--cai-color-text-muted)" ta="right">
        {prompt.length} / {promptMax}
      </Text>
      <Errors job={job} uploads={[character.upload, motion.upload]} />
      {character.dialog}
      {motion.dialog}
      {generate.dialog}
    </GlassPanel>
  );
}

/**
 * A clone's file for a slot: a library file or an earlier job's output (a motion video made in
 * the Studio, issue 828 item 23); none for a template's example or an actor's portrait.
 */
function clonedFile(ref: TemplateSource | null | undefined): SourceRef | null {
  return ref?.kind === "asset" || ref?.kind === "job_output" ? ref : null;
}
